import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  reload,
  loadPage,
  setChangedTimestamp,
  reset,
  reset as resetPage,
} from "./assetsSlice";
import { deleteAsset, updateAsset, restoreAsset } from "../asset/assetSlice";
import { generateAssetDownloadURL } from "../../api/server";
import { usePermission } from "../../hooks/accessControl";
import { useLocation } from "react-router-dom";
import FullName from "../../components/tables/FullName";
import { fetchList } from "../../api/server";
import ActionsTable from "../../components/templates/ActionsTable";
import PdfViewerModal from "../../components/templates/PdfViewerModal";
import Surface from "../../components/layouts/Surface";
import Date from "../../components/tables/Date";
import { Typography, Button, Modal, Dropdown, Menu } from "antd";
import {
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Header from "./components/Header";
import { ReactComponent as BranchesImg } from "../../assets/no-branches.svg";
import {
  useAssetsCategories,
  useAssetsSubCategories,
  useAssetsStatuses,
} from "../../hooks";
import { useQuery } from "react-query";
import AssetUploadModal from "../../components/util/AssetUploadModal";
import SendMail from "./components/SendMail";
import History from "./components/History";
import { changeScope } from "./assetsSlice";
import { useParams } from "react-router-dom";
import moment from "moment";
import { saveAs } from "file-saver";

const { Text } = Typography;
const Assets = (props) => {
  const { resource, entityId, fromCrm, fromMain } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  let { branchId } = useParams();
  const assetsCategories = useAssetsCategories();
  const assetsSubCategories = useAssetsSubCategories();
  const assetsStatuses = useAssetsStatuses();
  const [modal, contextHolder] = Modal.useModal();
  const {
    page: assetsPage,
    scope,
    from,
    to,
    activeBranch,
    changedTimestamp,
    reloadTimestamp,
    showInactive,
  } = useSelector((state) => state.assets);
  const userScope = useSelector((state) => state.user.scopePrefix);
  const { current, pageSize, order, filter } = assetsPage;
  const [visible, setVisible] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [sendMailVisible, setSendMailVisible] = useState(false);
  const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
  const [activeFileContentType, setActiveFileContentType] = useState("");
  const [activeFileUrl, setActiveFileUrl] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [total, setTotal] = useState(null);
  const intl = useIntl();
  const branchPath = location.pathname.substring(
    0,
    location.pathname.lastIndexOf("/")
  );
  useEffect(() => {
    const scope = resource && entityId ? `${resource}/${entityId}/` : "";
    dispatch(changeScope(scope));

    return function cleanup() {
      dispatch(reset());
    };
  }, [resource, entityId, dispatch]);

  const onTextChange = (e) => {
    dispatch(
      loadPage({ current: 1, filter: { ...filter, freeText: e.target.value } })
    );
  };

  const scopePrefix = activeBranch
    ? `branches/${activeBranch}/${scope}`
    : `${userScope}${scope}`;

  const { isLoading, data } = useQuery(
    [
      assetsPage,
      scope,
      from,
      to,
      activeBranch,
      changedTimestamp,
      reloadTimestamp,
      showInactive,
      branchId,
      resource,
      entityId,
    ],
    async () => {
      const result = await fetchList("assets", {
        ...assetsPage,
        query: { include_deleted: showInactive },
        scopePrefix,
      });
      setTotal(result.data.total);
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: assetsPage !== null,
    }
  );

  const onEditFile = (record) => {
    setActiveRecord(record);
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
  };

  const onShowHistory = (record) => {
    setActiveRecord(record);
    setHistoryVisible(true);
  };

  const hideHistory = (record) => {
    setActiveRecord(null);
    setHistoryVisible(false);
  };

  const onShowSendMail = (record) => {
    setActiveRecord(record);
    setSendMailVisible(true);
  };

  const hideSendMail = (record) => {
    setActiveRecord(null);
    setSendMailVisible(false);
  };

  const hidePdfViewer = () => {
    setPdfViewerVisible(false);
    setActiveFileUrl(null);
    setActiveFileContentType("");
  };

  const onShowFile = async (f) => {
    const scope = branchId
      ? `/branches/${branchId}/${f.owner_type}/${f.owner_id}`
      : "";
    const respond = await generateAssetDownloadURL(scope, f.id);
    setActiveFileUrl(respond.data.url);
    setActiveFileContentType(f.content_type);
    setPdfViewerVisible(true);
  };

  const onDownloadFile = async (f) => {
    const scope = branchId
      ? `/branches/${branchId}/${f.owner_type}/${f.owner_id}`
      : "";
    const {
      data: { url },
    } = await generateAssetDownloadURL(scope, f.id);
    let blob = await fetch(url).then((r) => r.blob());
    saveAs(blob, f.name);
  };

  const onDeleteFile = async (record) => {
    const scope = branchId
      ? `/branches/${branchId}/${record.owner_type}/${record.owner_id}`
      : "";
    const params = {
      assetId: record.id,
      scope,
    };
    const title = intl.formatMessage({
      id: "app.documents.deleteConfirmTitle",
    });
    const content =
      intl.formatMessage({
        id: "app.documents.deleteConfirmContent",
      }) +
      " " +
      record.name +
      "?";
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return deletFile(params);
      },
      onCancel() {},
    });
  };

  const onRestoreFile = async (record) => {
    const scope = branchId
      ? `/branches/${branchId}/${record.owner_type}/${record.owner_id}`
      : "";
    const params = {
      assetId: record.id,
      scope,
    };
    await dispatch(restoreAsset(params));
    dispatch(reload());
    return dispatch(setChangedTimestamp(moment().format()));
  };

  const deletFile = async (params) => {
    await dispatch(deleteAsset(params));
    dispatch(reload());
    return dispatch(setChangedTimestamp(moment().format()));
  };

  const handleMenuClick = (e, record) => {
    switch (e.key) {
      case "0":
        onShowFile(record);
        break;
      case "1":
        onDownloadFile(record);
        break;
      case "2":
        onEditFile(record);
        break;
      case "3":
        onShowHistory(record);
        break;
      case "4":
        onShowSendMail(record);
        break;
      case "5":
        onDeleteFile(record);
        break;
      case "6":
        onRestoreFile(record);
        break;

      default:
        break;
    }
  };

  const hasEditorPermission = usePermission(branchId, "assets", "editor");
  const hasViewerPermission = usePermission(branchId, "assets", "viewer");
  const hasRestorePermission = usePermission(branchId, "assets", "restore");

  const getItems = (record, isDeleted) => {
    let items = [];
    const contentType = record?.content_type ? record?.content_type : "";
    const isSpported =
      contentType.includes("pdf") || contentType.includes("image");
    if (isSpported) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.show_file",
        }),
        key: "0",
        icon: <SearchOutlined />,
      });
    }

    if (hasViewerPermission && !isDeleted) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.download",
        }),
        key: "1",
        icon: <DownloadOutlined />,
      });
    }

    if (hasEditorPermission && !isDeleted) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.edit",
        }),
        key: "2",
        icon: <EditOutlined />,
      });
    }

    if (!isDeleted) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.show_history",
        }),
        key: "3",
        icon: <EditOutlined />,
      });
    }

    if (hasViewerPermission && !isDeleted) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.send_mail",
        }),
        key: "4",
        icon: <EditOutlined />,
      });
    }

    if (hasEditorPermission && !isDeleted) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.delete",
        }),
        key: "5",
        icon: <DeleteOutlined style={{ color: "red" }} />,
        style: { color: "red" },
      });
    }

    if (isDeleted && hasRestorePermission) {
      items.push({
        label: intl.formatMessage({
          id: "app.assets.restore",
        }),
        key: "6",
        icon: <EditOutlined style={{ color: "red" }} />,
      });
    }
    return items;
  };

  return (
    <Surface smallPadding={true}>
      {contextHolder}
      <Header
        reload={reload}
        onTextChange={onTextChange}
        textFilter={filter.freeText}
        entityId={entityId}
      />
      <ActionsTable
        tableItems={[
          {
            title: "name",
            responsive: ["md"],
            sortIndex: "name",
            ellipsis: true,
          },
          {
            title: "owner_name",
            responsive: ["md"],
            sortIndex: "owner_surename",
            sorter: true,
            ellipsis: true,
            render: (row) => {
              const name = {
                first_name: row.record.owner_first_name,
                surename: row.record.owner_surename,
              };
              return row.record ? (
                <FullName
                  record={name}
                  customLocation={`${branchPath}/${row.record.owner_type}/${row.record.owner_id}`}
                />
              ) : null;
            },
          },

          {
            title: "owner_type",
            responsive: ["md"],
            filters: [
              {
                value: "patients",
                text: intl.formatMessage({
                  id: `app.sidebar.patients`,
                }),
              },
              {
                value: "employees",
                text: intl.formatMessage({
                  id: `app.sidebar.employees`,
                }),
              },
            ],
            sortIndex: "owner_type",
            render(text) {
              return (
                <Text>
                  {text.record &&
                    intl.formatMessage({
                      id: `app.sidebar.${text.record["owner_type"]}`,
                    })}
                </Text>
              );
            },
            ellipsis: true,
          },

          {
            title: "status_name",
            responsive: ["md"],
            sortIndex: "status_id",
            // sorter: true,
            filters: assetsStatuses.map((i) => ({
              text: i.name,
              value: i.id,
            })),
            ellipsis: true,
          },
          {
            title: "created_at",
            responsive: ["xxl"],
            render(text) {
              return (
                <Date text={text.record["created_at"]} format={"DD/MM/YYYY"} />
              );
            },
            ellipsis: true,
            sortIndex: "created_at",
            sorter: true,
          },
          {
            title: "updated_at",
            responsive: ["xl"],
            render(text) {
              return (
                <Date text={text.record["updated_at"]} format={"DD/MM/YYYY"} />
              );
            },
            sortIndex: "updated_at",
            ellipsis: true,
          },
          {
            title: "updated_by_name",
            responsive: ["xl"],
            ellipsis: true,
          },
          {
            title: "category_name",
            responsive: ["xl"],
            sortIndex: "category_id",
            // sorter: true,
            filters: assetsCategories.map((i) => ({
              text: i.name,
              value: i.id,
            })),
            ellipsis: true,
          },
          {
            title: "sub_category_name",
            responsive: ["xl"],
            sortIndex: "sub_category_id",
            // sorter: true,
            filters: assetsSubCategories.map((i) => ({
              text: i.name,
              value: i.id,
            })),
            ellipsis: true,
          },
          {
            title: "actions",
            responsive: ["md"],
            width: "120px",
            render: ({ record }) => {
              const isDeleted = record.deleted_at !== null;
              const items = getItems(record, isDeleted);
              return (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items,
                    onClick: (e) => handleMenuClick(e, record),
                  }}
                >
                  <Button type="link">
                    {intl.formatMessage({
                      id: "app.employees.options",
                    })}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              );
            },
          },
        ]}
        checkActive={(rec) => rec.deleted_at === null}
        category={"assets"}
        resource="assets"
        current={current}
        pageSize={pageSize}
        total={total}
        order={order}
        loading={isLoading}
        textFilter={filter.freeText}
        reload={reload}
        loadPage={loadPage}
        resetPage={resetPage}
        items={data || []}
        filtersType={{
          category_id: "number",
          sub_category_id: "number",
          owner_type: "string",
          status_id: "number",
        }}
        onTextChange={onTextChange}
        showheader={false}
        emptyImage={<BranchesImg />}
        emptytext={<FormattedMessage id="app.tables.noAssets" />}
        skipInitialLoad={true}
        scroll={
          fromMain
            ? window.innerHeight - 388
            : fromCrm
            ? window.innerHeight - 752
            : window.innerHeight - 706
        }
      />
      <AssetUploadModal
        visible={visible}
        closeModal={closeModal}
        mode={"edit"}
        file={null}
        loading={isLoading}
        loadAssets={loadPage}
        updateAsset={updateAsset}
        activeRecord={activeRecord}
        resource={activeRecord && activeRecord["owner_type"]}
        scope={scope}
        branchId={branchId}
      />
      <History
        activeRecord={activeRecord}
        visible={historyVisible}
        hide={hideHistory}
        scope={scope}
        branchId={branchId}
      />
      <SendMail
        activeRecord={activeRecord}
        visible={sendMailVisible}
        hide={hideSendMail}
        scope={scope}
        branchId={branchId}
      />
      <PdfViewerModal
        visible={pdfViewerVisible}
        onHide={hidePdfViewer}
        contentType={activeFileContentType}
        activeFileUrl={activeFileUrl}
      />
    </Surface>
  );
};

export default Assets;
